import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { AlertTriangle } from "lucide-react";
import { useSEO } from "@/lib/useSEO";

const PAGES = {
  terms: {
    title: "Terms & Conditions",
    body: [
      "These Terms & Conditions govern participation in Roamies trips, drops, and Roamies IRL events.",
      "This is a placeholder document. Final terms — covering eligibility (ages 18–45), booking, payment, conduct, and liability — are being drafted and must be reviewed by the owner and qualified legal counsel before bookings are accepted.",
      "By joining the waitlist you agree to receive trip and event communications from Roamies. You can unsubscribe at any time.",
    ],
  },
  privacy: {
    title: "Privacy Policy",
    body: [
      "Roamies collects only the information you give us: your name, email, optional phone, age, city, Instagram handle, and your trip interests.",
      "We use it to contact you about drops and events you asked about, and to run trips safely. We do not sell your data.",
      "This is a placeholder document pending owner/legal review. A full policy covering data retention, third-party processors (email delivery, analytics), and your rights will be published here before bookings open.",
      "Questions about your data? Use the contact form and we'll respond.",
    ],
  },
  cancellation: {
    title: "Cancellation & Refund Policy",
    body: [
      "Roamies is currently waitlist-only — no payments are taken on this site, so there is nothing to refund yet.",
      "Before booking opens, a full cancellation and refund policy (deposit rules, deadlines, and what happens if a trip is rescheduled or cancelled) will be published here and linked at checkout.",
      "This placeholder requires owner/legal review before any payments are accepted.",
    ],
  },
  waiver: {
    title: "Travel Waiver",
    body: [
      "Group travel involves inherent risks — physical activity, transportation, and shared spaces among them.",
      "Before any trip departs, every Roamie will review and sign a participation waiver covering these risks, emergency contacts, and conduct expectations.",
      "This is a placeholder document and must be drafted/reviewed by qualified legal counsel before the first trip.",
    ],
  },
  safety: {
    title: "Safety Information",
    body: [
      "Roamies trips are built around small curated groups with a dedicated host, a clear itinerary, and a group chat that opens before arrival.",
      "Before departure, hosts collect emergency contact details from every traveler and share how to reach them during the trip.",
      "We verify that travelers confirm they are within the 18–45 age range when joining the waitlist.",
      "What's included and excluded is listed transparently on every trip page. Travel insurance is not included — we recommend arranging your own.",
      "This page describes our current practices; it is not a guarantee, and a full safety policy will be reviewed before bookings open.",
    ],
  },
};

export default function LegalPage() {
  const { slug } = useParams();
  const page = PAGES[slug] || PAGES.terms;
  useSEO(`${page.title} — Roamies`, `${page.title} for Roamies small-group trips and events.`);

  return (
    <div className="py-16 sm:py-24" data-testid={`legal-page-${slug}`}>
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <Link to="/" className="text-xs font-bold uppercase tracking-widest text-ink/50 hover:text-flame transition-colors">
            ← Roamies
          </Link>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase leading-[0.95] mt-6" data-testid="legal-title">
            {page.title}
          </h1>
          <div className="mt-6 flex items-start gap-3 bg-flame/10 border border-flame/30 p-4" data-testid="legal-review-notice">
            <AlertTriangle className="w-4 h-4 text-flame shrink-0 mt-0.5" />
            <p className="text-xs text-ink/70 leading-relaxed">
              Placeholder copy — this page requires owner and legal review before bookings are accepted.
            </p>
          </div>
          <div className="mt-10 space-y-5">
            {page.body.map((para, i) => (
              <p key={i} className="text-ink/75 leading-relaxed">{para}</p>
            ))}
          </div>
          <p className="mt-12 text-xs text-ink/40">Last updated: {new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" })}</p>
        </motion.div>
      </div>
    </div>
  );
}
